import React, { ReactNode, Suspense } from 'react';
import Router from './Router';
import {
  isCapacitor,
  ThemeProvider as ThemeProviderZignalyUI,
  I18nextProvider as I18nextProviderZignalyUI,
  // has to be imported from the same module from where we call the show toast
  ToastContainer,
} from '@zignaly-open/ui';
import { ThemeChartGradients } from '@zignaly-open/ui/charts';
import { ThemeProvider as ThemeProviderMui } from '@mui/material';
import ModalProvider from 'mui-modal-provider';
import { BrowserRouter } from 'react-router-dom';
import { persistor, store } from './apis/store';
import { Provider } from 'react-redux';
import GlobalStyle from './styles';
import { PersistGate } from 'redux-persist/integration/react';
import Header from './components/Navigation/Header';
import UpdateChecker from './components/Navigation/Checkers/UpdateChecker';
import UserKycChecker from './components/Navigation/Checkers/UserKycChecker';
import DateLocaleFixer from './components/Navigation/DateLocaleFixer';
import Tracker from './components/Navigation/Tracker/Tracker';
import useReferralCookie from 'util/hooks/useReferralCookie';
import BottomNavigation from 'components/Navigation/BottomNavigation';
import { zigSuspenseFallback } from './util/suspense';
import ZModal from './components/ZModal';
import ZigErrorBoundary from './util/ZigErrorBoundary';
import './util/i18n/i18nextWhitelabel';
import i18next from './util/i18n/i18next';
import MissingExchangeChecker from 'components/Navigation/Checkers/MissingExchangeChecker';
import { useWlInstanceThemeWithDebugStyling } from './theme';
import { useMaybeUpdateServiceWorkerOnClose } from './sw/util';
import CapacitorUpdateChecker from 'components/Navigation/Checkers/CapacitorUpdateChecker';
import Maintenance from 'views/Error/Maintenance';
import {
  useIsCloudflareBlocked,
  useIsMaintenance,
  useMaybeDelayedCheckServerStatus,
} from './apis/appMeta/use';
import { GoogleOAuthProvider } from '@react-oauth/google';
import CloudflareBlocked from './views/Error/CloudflareBlocked';
import { useIsAuthenticated } from './apis/user/use';
import { googleClientId, shouldUseServiceWorker } from './whitelabel';

import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import rtlPlugin from '@mui/stylis-plugin-rtl';
import {
  useDocumentDirection,
  useUpdateDocumentRtl,
} from './util/hooks/useUpdateDocumentRtl';

const PwaInstall = React.lazy(() => import('util/pwaInstall'));
import('./sentry');

const rtlCache = createCache({
  key: 'muirtl',
  stylisPlugins: [rtlPlugin],
});

const lrtCache = createCache({
  key: 'muilrt',
  stylisPlugins: [],
});

export const WrappedInProviders: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const themeMui = useWlInstanceThemeWithDebugStyling();
  const dir = useDocumentDirection();
  return (
    <Provider store={store}>
      <I18nextProviderZignalyUI i18n={i18next}>
        {/* We intentionally pass the invalid client id string here because otherwise the app crashes when using a hook */}
        <GoogleOAuthProvider clientId={googleClientId || 'disabled'}>
          <CacheProvider value={dir === 'rtl' ? rtlCache : lrtCache}>
            <ThemeProviderZignalyUI theme={themeMui}>
              <ThemeProviderMui theme={themeMui}>
                <GlobalStyle />
                <ToastContainer
                  position={dir === 'ltr' ? 'top-right' : 'top-left'}
                  autoClose={5000}
                  hideProgressBar
                  closeOnClick
                  pauseOnFocusLoss
                  draggable
                  closeButton={false}
                  pauseOnHover
                  theme='dark'
                  style={{ paddingTop: 'env(safe-area-inset-top)' }}
                />
                <PersistGate persistor={persistor}>
                  <BrowserRouter>
                    <ZigErrorBoundary>
                      <Suspense fallback={zigSuspenseFallback}>
                        <ModalProvider
                          fallback={<ZModal allowUnauth wide open isLoading />}
                        >
                          {children}
                        </ModalProvider>
                      </Suspense>
                    </ZigErrorBoundary>
                  </BrowserRouter>
                </PersistGate>
              </ThemeProviderMui>
            </ThemeProviderZignalyUI>
          </CacheProvider>
        </GoogleOAuthProvider>
      </I18nextProviderZignalyUI>
    </Provider>
  );
};

function App() {
  useReferralCookie();
  useMaybeUpdateServiceWorkerOnClose();
  const isMaintenance = useIsMaintenance();
  const isCloudflareBlocked = useIsCloudflareBlocked();
  useMaybeDelayedCheckServerStatus();
  const isAuthenticated = useIsAuthenticated();
  useUpdateDocumentRtl();

  return (
    <>
      {!isMaintenance && !isCloudflareBlocked && <Header />}
      <Suspense fallback={zigSuspenseFallback}>
        <ZigErrorBoundary>
          {isMaintenance ? (
            <Maintenance />
          ) : isCloudflareBlocked ? (
            <CloudflareBlocked />
          ) : (
            <>
              <Tracker />
              {isCapacitor ? <CapacitorUpdateChecker /> : <UpdateChecker />}
              {isAuthenticated && <UserKycChecker />}
              <MissingExchangeChecker />
              <DateLocaleFixer />
              <ThemeChartGradients />
              <Router />
            </>
          )}
        </ZigErrorBoundary>
        {!isMaintenance && !isCloudflareBlocked && <BottomNavigation />}
      </Suspense>
    </>
  );
}

export default () => (
  <WrappedInProviders>
    <App />
    <React.Suspense>{shouldUseServiceWorker && <PwaInstall />}</React.Suspense>
  </WrappedInProviders>
);
